import { computed, type Ref } from 'vue';
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import http from '@/services/http';
import { toast } from 'vue-sonner';
import type { PageDefinition, UserPageGrantState } from '@/types/types';

/**
 * API client for the per-user page grants (SAU-132). Pages are seeded on
 * the backend — there is no page CRUD here. A page controls only the
 * nav-bar button + route access; granting one does NOT touch the user's
 * model permissions, so this client never invalidates the model-matrix
 * cache.
 */

const PAGES_KEY = ['pages'] as const;
const userPagesKey = (userId: number | null) => ['user-pages', userId] as const;

export function usePages() {
  return useQuery<PageDefinition[]>({
    queryKey: PAGES_KEY,
    queryFn: () =>
      http.get<PageDefinition[]>('/api/permissions/pages').then((response) => response.data),
  });
}

export function useUserPageState(userId: Ref<number | null>) {
  return useQuery<UserPageGrantState[]>({
    queryKey: computed(() => userPagesKey(userId.value)),
    queryFn: () =>
      http
        .get<UserPageGrantState[]>(`/api/permissions/${userId.value}/pages`)
        .then((response) => response.data),
    enabled: computed(() => userId.value !== null),
  });
}

/**
 * Tracks the genuine pre-optimistic server snapshot per (userId, pageId)
 * while any setUserPageGrant mutation is in flight for that pair. Defends
 * against the rapid-double-toggle race: without this, the second
 * `onMutate` would capture the optimistic mid-state as its rollback
 * target, so a second-PUT failure would restore the cache to a value the
 * server never actually held.
 *
 * Refcounted because 3+ rapid toggles all queue concurrent mutations —
 * the entry must outlive every one of them so each `onError` can roll
 * back to the same true baseline. Cleared once the refcount hits zero.
 *
 * Module-scoped intentionally — survives component re-mount. The keys
 * are user/page ids which are stable across the app lifetime.
 */
type PendingPreviousEntry = {
  previous: UserPageGrantState[] | undefined;
  refCount: number;
};
const pendingPreviousByPair = new Map<string, PendingPreviousEntry>();

function pendingPreviousKey(userId: number, pageId: number): string {
  return `${userId}:${pageId}`;
}

export function useSetUserPageGrant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      pageId,
      granted,
    }: {
      userId: number;
      pageId: number;
      granted: boolean;
    }) =>
      http
        .put<UserPageGrantState>(`/api/permissions/${userId}/pages/${pageId}`, {
          granted,
        })
        .then((response) => response.data),
    /**
     * Optimistic update — we patch the cache synchronously so the
     * controlled checkbox reads the new state on the very next render,
     * avoiding a flicker while the PUT round-trips.
     *
     * Race-safety: the rollback target (`previous`) is the genuine server
     * snapshot, not whatever the cache holds at this moment — if a prior
     * mutation for the same (userId, pageId) is still in-flight, its
     * optimistic patch has already moved the cache off the truth. We pull
     * the in-flight's snapshot from {@link pendingPreviousByPair} so every
     * concurrent mutation rolls back to the same true baseline on error.
     */
    onMutate: async ({ userId, pageId, granted }) => {
      const key = userPagesKey(userId);
      const pairKey = pendingPreviousKey(userId, pageId);
      // Cancel any in-flight refetch so it can't clobber our patch.
      await queryClient.cancelQueries({ queryKey: key });

      const existingPending = pendingPreviousByPair.get(pairKey);
      const previous = existingPending
        ? existingPending.previous
        : queryClient.getQueryData<UserPageGrantState[]>(key);
      pendingPreviousByPair.set(pairKey, {
        previous,
        refCount: (existingPending?.refCount ?? 0) + 1,
      });

      queryClient.setQueryData<UserPageGrantState[]>(key, (old) => {
        const list = old ?? [];
        const existing = list.find((row) => row.page_id === pageId);
        if (existing) {
          return list.map((row) => (row.page_id === pageId ? { ...row, granted } : row));
        }
        // First-time toggle for a page that wasn't in the user's state
        // payload yet — append it so the checkbox has something to bind to.
        return [...list, { page_id: pageId, granted }];
      });
      return { previous, key, pairKey };
    },
    onError: (_error, _variables, context) => {
      // Roll the cache back to the genuine server snapshot captured at the
      // first in-flight mutation's onMutate. Every concurrent mutation
      // rolls back to the same value, so the cache lands on truth
      // regardless of which (or how many) failed.
      if (context && context.previous !== undefined) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error('Could not update the page.', { duration: 5000 });
    },
    onSettled: (_data, _error, variables, context) => {
      // Decrement the in-flight refcount; drop the snapshot once every
      // concurrent mutation for this (userId, pageId) has settled.
      if (context?.pairKey) {
        const entry = pendingPreviousByPair.get(context.pairKey);
        if (entry) {
          if (entry.refCount <= 1) {
            pendingPreviousByPair.delete(context.pairKey);
          } else {
            pendingPreviousByPair.set(context.pairKey, {
              ...entry,
              refCount: entry.refCount - 1,
            });
          }
        }
      }
      // Reconcile against the truth. Only the page-state cache is touched —
      // page grants no longer materialise model bits, so the model-matrix
      // (user-permissions) cache is deliberately left alone.
      queryClient.invalidateQueries({ queryKey: userPagesKey(variables.userId) });
    },
  });
}
